import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Slider } from "./ui/slider";
import { Crop, Square, Brush, Check, Undo2, Maximize2, RectangleVertical } from "lucide-react";

// Target output aspect ratio (portrait 9:16)
const TARGET_W = 720;
const TARGET_H = 1280;
const RATIO = TARGET_W / TARGET_H;

const PhotoEditor = ({ open, onClose, file, onSave }) => {
  const [src, setSrc] = useState(null);
  const [tool, setTool] = useState("crop"); // crop | blur | pixelate
  const [brush, setBrush] = useState(28);
  const [intensity, setIntensity] = useState(14);
  const [drawing, setDrawing] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [history, setHistory] = useState([]);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const imgRef = useRef(null);

  const pushHistory = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const snap = ctx.getImageData(0, 0, c.width, c.height);
    setHistory((h) => {
      const next = [...h, { data: snap, w: c.width, h: c.height }];
      if (next.length > 20) next.shift();
      return next;
    });
  }, []);

  const undo = () => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const last = h[h.length - 1];
      const c = canvasRef.current;
      c.width = last.w; c.height = last.h;
      const ctx = c.getContext("2d");
      ctx.putImageData(last.data, 0, 0);
      const o = overlayRef.current;
      o.width = last.w; o.height = last.h;
      setCrop({ x: 0, y: 0, w: 0, h: 0 });
      return h.slice(0, -1);
    });
  };

  // Draw original image (up to maxW=720 wide) into the canvas keeping its aspect.
  const drawOriginal = (img, c) => {
    const maxW = 720;
    const r = Math.min(1, maxW / img.naturalWidth);
    const w = Math.round(img.naturalWidth * r);
    const h = Math.round(img.naturalHeight * r);
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
  };

  const drawCoverFromImg = (img, c) => {
    c.width = TARGET_W; c.height = TARGET_H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#1A2118";
    ctx.fillRect(0, 0, TARGET_W, TARGET_H);
    const srcRatio = img.naturalWidth / img.naturalHeight;
    let sx, sy, sw, sh;
    if (srcRatio > RATIO) {
      sh = img.naturalHeight; sw = sh * RATIO;
      sx = (img.naturalWidth - sw) / 2; sy = 0;
    } else {
      sw = img.naturalWidth; sh = sw / RATIO;
      sx = 0; sy = (img.naturalHeight - sh) / 2;
    }
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, TARGET_W, TARGET_H);
  };

  const drawContainFromImg = (img, c) => {
    c.width = TARGET_W; c.height = TARGET_H;
    const ctx = c.getContext("2d");
    const srcRatio = img.naturalWidth / img.naturalHeight;
    let bx, by, bw, bh;
    if (srcRatio > RATIO) { bh = TARGET_H * 1.1; bw = bh * srcRatio; }
    else { bw = TARGET_W * 1.1; bh = bw / srcRatio; }
    bx = (TARGET_W - bw)/2; by = (TARGET_H - bh)/2;
    ctx.filter = "blur(28px) brightness(0.55)";
    ctx.drawImage(img, bx, by, bw, bh);
    ctx.filter = "none";
    let w, h;
    if (srcRatio > RATIO) { w = TARGET_W; h = TARGET_W / srcRatio; }
    else { h = TARGET_H; w = TARGET_H * srcRatio; }
    ctx.drawImage(img, (TARGET_W - w) / 2, (TARGET_H - h) / 2, w, h);
  };

  // Draw current canvas content using cover/contain into 9:16 (so applies after edits too)
  const refit = (mode) => {
    const c = canvasRef.current;
    if (!c) return;
    pushHistory();
    const tmp = document.createElement("canvas");
    tmp.width = c.width; tmp.height = c.height;
    tmp.getContext("2d").drawImage(c, 0, 0);
    const imgLike = { naturalWidth: tmp.width, naturalHeight: tmp.height };
    // Build a temp image element via canvas as source
    if (mode === "cover") drawCoverFromImg(Object.assign(tmp, imgLike), c);
    else drawContainFromImg(Object.assign(tmp, imgLike), c);
    const o = overlayRef.current;
    o.width = c.width; o.height = c.height;
    setCrop({ x: 0, y: 0, w: 0, h: 0 });
  };

  useEffect(() => {
    if (!file) { setSrc(null); setHistory([]); return; }
    const url = URL.createObjectURL(file);
    setSrc(url);
    setHistory([]);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onImgLoad = () => {
    const img = imgRef.current;
    const c = canvasRef.current;
    drawOriginal(img, c);
    const o = overlayRef.current;
    o.width = c.width; o.height = c.height;
  };

  // Painting helpers
  const getPos = (e) => {
    const c = overlayRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    const scaleX = c.width / rect.width;
    const scaleY = c.height / rect.height;
    return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY };
  };

  const pixelateAt = (cx, cy) => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const size = brush * 2;
    const block = Math.max(4, intensity);
    const x = Math.max(0, Math.round(cx - size / 2));
    const y = Math.max(0, Math.round(cy - size / 2));
    const sw = Math.min(size, c.width - x);
    const sh = Math.min(size, c.height - y);
    if (sw <= 0 || sh <= 0) return;
    const imgData = ctx.getImageData(x, y, sw, sh);
    const data = imgData.data;
    for (let by = 0; by < sh; by += block) {
      for (let bx = 0; bx < sw; bx += block) {
        const i = ((by * sw) + bx) * 4;
        const r = data[i], g = data[i + 1], b = data[i + 2];
        for (let yy = 0; yy < block && by + yy < sh; yy++) {
          for (let xx = 0; xx < block && bx + xx < sw; xx++) {
            const j = (((by + yy) * sw) + (bx + xx)) * 4;
            data[j] = r; data[j + 1] = g; data[j + 2] = b;
          }
        }
      }
    }
    ctx.putImageData(imgData, x, y);
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    if (tool === "crop") {
      const p = getPos(e);
      setCrop({ x: p.x, y: p.y, w: 0, h: 0 });
      setDrawing(true);
      return;
    }
    pushHistory();
    setDrawing(true);
    const p = getPos(e);
    pixelateAt(p.x, p.y);
  };
  const onPointerMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = getPos(e);
    if (tool === "crop") {
      setCrop((c) => ({ x: c.x, y: c.y, w: p.x - c.x, h: p.y - c.y }));
    } else {
      pixelateAt(p.x, p.y);
    }
  };
  const onPointerUp = () => setDrawing(false);

  const applyCrop = () => {
    let { x, y, w, h } = crop;
    if (w < 0) { x += w; w = -w; }
    if (h < 0) { y += h; h = -h; }
    if (w < 10 || h < 10) return;
    pushHistory();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const data = ctx.getImageData(x, y, w, h);
    c.width = w; c.height = h;
    ctx.putImageData(data, 0, 0);
    const o = overlayRef.current;
    o.width = w; o.height = h;
    setCrop({ x: 0, y: 0, w: 0, h: 0 });
  };

  // Render crop rectangle overlay
  useEffect(() => {
    const o = overlayRef.current;
    if (!o) return;
    const ctx = o.getContext("2d");
    ctx.clearRect(0, 0, o.width, o.height);
    if (tool === "crop" && Math.abs(crop.w) > 2 && Math.abs(crop.h) > 2) {
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.fillRect(0, 0, o.width, o.height);
      let { x, y, w, h } = crop;
      if (w < 0) { x += w; w = -w; }
      if (h < 0) { y += h; h = -h; }
      ctx.clearRect(x, y, w, h);
      ctx.strokeStyle = "#D4AF37";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  }, [crop, tool]);

  const save = () => {
    const c = canvasRef.current;
    c.toBlob((blob) => onSave(blob), "image/jpeg", 0.9);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#FAF9F6]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Editor de foto</DialogTitle>
        </DialogHeader>

        {src && (
          <>
            <img ref={imgRef} src={src} alt="" onLoad={onImgLoad} style={{ display: "none" }} />

            <div className="relative bg-black/5 rounded-lg overflow-hidden flex justify-center max-h-[60vh]">
              <div className="relative" style={{ maxHeight: "60vh" }}>
                <canvas
                  ref={canvasRef}
                  className="block max-w-full max-h-[60vh] w-auto h-auto select-none"
                  onContextMenu={(e) => e.preventDefault()}
                />
                <canvas
                  ref={overlayRef}
                  className="absolute inset-0 w-full h-full select-none"
                  style={{ touchAction: "none", cursor: tool === "crop" ? "crosshair" : "default" }}
                  onMouseDown={onPointerDown}
                  onMouseMove={onPointerMove}
                  onMouseUp={onPointerUp}
                  onMouseLeave={onPointerUp}
                  onTouchStart={onPointerDown}
                  onTouchMove={onPointerMove}
                  onTouchEnd={onPointerUp}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button data-testid="tool-crop" onClick={() => setTool("crop")} className={`btn-ghost text-sm inline-flex items-center gap-1 ${tool==='crop'?'!bg-[#364531] !text-[#FAF9F6]':''}`}><Crop size={14}/> Recortar</button>
              <button data-testid="tool-blur" onClick={() => setTool("blur")} className={`btn-ghost text-sm inline-flex items-center gap-1 ${tool==='blur'?'!bg-[#364531] !text-[#FAF9F6]':''}`}><Brush size={14}/> Difuminar</button>
              <button data-testid="tool-pixel" onClick={() => setTool("pixelate")} className={`btn-ghost text-sm inline-flex items-center gap-1 ${tool==='pixelate'?'!bg-[#364531] !text-[#FAF9F6]':''}`}><Square size={14}/> Pixelar</button>

              <div className="w-px h-6 bg-[#E5E2D9] mx-1" />

              <button data-testid="undo-btn" onClick={undo} disabled={!history.length}
                className="btn-ghost text-sm inline-flex items-center gap-1 disabled:opacity-40">
                <Undo2 size={14}/> Deshacer
              </button>

              {tool === "crop" && (
                <button data-testid="apply-crop" onClick={applyCrop} disabled={Math.abs(crop.w) < 10 || Math.abs(crop.h) < 10}
                        className="btn-primary text-sm disabled:opacity-40">
                  Aplicar recorte
                </button>
              )}

              <div className="w-px h-6 bg-[#E5E2D9] mx-1" />

              <button data-testid="fit-cover" onClick={() => refit("cover")} className="btn-ghost text-sm inline-flex items-center gap-1" title="Adapta el lienzo actual a 9:16 cortando bordes">
                <RectangleVertical size={14}/> 9:16 recortar
              </button>
              <button data-testid="fit-contain" onClick={() => refit("contain")} className="btn-ghost text-sm inline-flex items-center gap-1" title="Adapta el lienzo actual a 9:16 con fondo difuminado">
                <Maximize2 size={14}/> 9:16 encajar
              </button>

              {tool !== "crop" && (
                <div className="flex items-center gap-3 ml-auto">
                  <div className="text-xs text-[#6B6A63]">Pincel</div>
                  <Slider value={[brush]} onValueChange={(v)=>setBrush(v[0])} min={10} max={80} step={2} className="w-24" />
                  <div className="text-xs text-[#6B6A63]">Intensidad</div>
                  <Slider value={[intensity]} onValueChange={(v)=>setIntensity(v[0])} min={4} max={40} step={2} className="w-24" />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
              <button data-testid="editor-save" onClick={save} className="btn-primary inline-flex items-center gap-1"><Check size={16}/> Guardar y subir</button>
            </div>
            <p className="text-xs text-[#6B6A63]">Recortar: arrastra para seleccionar área y haz clic en "Aplicar recorte". Luego puedes usar 9:16 recortar/encajar para estandarizar el formato final.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhotoEditor;
